import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { EntityReviewService, ReviewMechanism } from '@/domain/shared/services/EntityReviewService';
import { toast } from '@/components/ui/use-toast';
import { httpRequest } from '@/lib/http-client';

export function ComplianceSettings() {
  const [threshold, setThreshold] = useState<number>(0.7);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const entityReviewService = EntityReviewService.getInstance();

  useEffect(() => {
    // Load the current settings when component mounts
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        // Load the current threshold
        const data = await httpRequest('/api/compliance/settings');
        setThreshold(data.threshold);
      } catch (error) {
        console.error('Error loading compliance settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadSettings();
  }, []);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Always use opensanctions
      entityReviewService.setDefaultMechanism('opensanctions');
      
      // Save threshold
      await httpRequest('/api/compliance/settings', {
        method: 'PUT',
        data: {
          threshold
        }
      });
      
      toast({
        title: "Settings saved",
        description: `Compliance settings updated successfully`,
      });
    } catch (error) {
      console.error('Error saving compliance settings:', error);
      toast({
        title: "Error",
        description: "Failed to save compliance settings",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Compliance Settings</CardTitle>
        <CardDescription>
          Configure compliance check settings for entity reviews
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="review-mechanism">
            <AccordionTrigger>Review Mechanism</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  The system performs automated compliance checks
                </p>

                <div className="rounded-md border p-3">
                  <h4 className="font-medium">Sanctions Screening</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Entities are screened against global sanctions lists, law enforcement databases, and politically exposed persons (PEPs) registries.
                  </p>
                  <div className="mt-2 text-sm">
                    <div><span className="font-medium">Data Type:</span> API-based real-time checks</div>
                    <div><span className="font-medium">Update Frequency:</span> Real-time (always current)</div>
                    <div><span className="font-medium">Available Collections:</span> sanctions, peps, default</div>
                    <div><span className="font-medium">Entity Types:</span> Person, Company, Vessel, Aircraft, Address</div>
                    <div><span className="font-medium">Data Points:</span> Names, aliases, birth dates, nationalities, positions, sanctions programs, and more</div>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="match-threshold">
            <AccordionTrigger>Match Threshold</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Set the confidence threshold for flagging entities. Higher values reduce false positives but may miss some matches.
                </p>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Confidence Threshold: {(threshold * 100).toFixed(0)}%</Label>
                    <span className="text-xs text-muted-foreground">
                      {threshold < 0.5 ? 'More Sensitive' : threshold > 0.8 ? 'Less Sensitive' : 'Balanced'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[threshold * 100]}
                      min={10}
                      max={95}
                      step={5}
                      onValueChange={(value) => setThreshold(value[0] / 100)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      min={10}
                      max={95}
                      value={(threshold * 100).toFixed(0)}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value >= 10 && value <= 95) {
                          setThreshold(value / 100);
                        }
                      }}
                      className="w-20"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-md border p-2">
                    <div className="font-medium">Low (30%)</div>
                    <div className="text-xs text-muted-foreground">More matches, more false positives</div>
                  </div>
                  <div className="rounded-md border p-2">
                    <div className="font-medium">Medium (70%)</div>
                    <div className="text-xs text-muted-foreground">Balanced approach</div>
                  </div>
                  <div className="rounded-md border p-2">
                    <div className="font-medium">High (90%)</div>
                    <div className="text-xs text-muted-foreground">Fewer matches, fewer false positives</div>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        
        <div className="mt-6">
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
} 